import { useEffect } from 'react';
import { PricingCards } from '~/features/shared/components/pricing-cards';
import { RenewPackageModal } from '~/features/shared/components/renew-package-modal';
import { PaymentQR } from '~/features/shared/components/payment-qr';
import { useAuth, useModal } from '~/hooks';
import { useFormModalStore } from '~/stores';

/**
 * LoginLockedModalTrigger
 * Tự động mở đúng modal sẵn có khi tài khoản bị khoá (is_login_locked = true):
 *  - id_product = 1  → Modal nâng cấp gói (PricingCards)
 *  - id_product != 1 → Modal gia hạn gói (RenewPackageModal → PaymentQR)
 *
 * Khóa chặt không cho đóng dưới mọi hình thức:
 *  - Chặn phím Escape ở capture phase toàn cục
 *  - isDismissable: false, isKeyboardDismissDisabled: true
 *  - Tự động mở lại ngay lập tức nếu modalStack bị đóng
 */
export const LoginLockedModalTrigger = ({
	mode,
}: {
	mode: 'upgrade' | 'renew';
}) => {
	const { user } = useAuth();
	const { openDetailModal, closeModal } = useModal();
	const modalStack = useFormModalStore((state) => state.modalStack);

	// ── 1. Chặn phím ESC toàn cục ở capture phase ──────────────────────
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' || e.keyCode === 27) {
				e.preventDefault();
				e.stopPropagation();
				e.stopImmediatePropagation?.();
			}
		};

		window.addEventListener('keydown', handleKeyDown, true);
		return () => window.removeEventListener('keydown', handleKeyDown, true);
	}, []);

	// ── Mở modal gia hạn + QR thanh toán ────────────────────────────────
	const openRenewModal = () => {
		const handleProceedPayment = (renewData: {
			idProduct?: number;
			productCode?: string;
			billingCycle: '6' | '12';
			totalAmount: number;
			packageAmount: number;
			extraStorage?: number;
			extraStorageAmount?: number;
		}) => {
			closeModal();
			setTimeout(() => {
				openDetailModal(
					{
						id: renewData.idProduct || user?.id_product,
						product_code: renewData.productCode || user?.product_code,
						users_no: user?.users_package,
						storage: user?.storage_package,
						display_amount: renewData.totalAmount,
						billing_cycle: renewData.billingCycle,
						extra_storage: renewData.extraStorage,
					},
					{
						size: '4xl',
						modalProps: {
							isDismissable: false,
							isDismissible: false,
							isKeyboardDismissDisabled: true,
							hideCloseButton: true,
							classNames: {
								base: 'p-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden [&_button[aria-label="Đóng"]]:hidden',
								wrapper: 'p-0',
								body: 'p-0',
								closeButton: 'hidden',
							},
						},
						renderContent: ({ data }: any) => (
							<PaymentQR
								product={data}
								user={user}
								mode="renew"
								onClose={() => openRenewModal()} // quay lại step chọn kỳ hạn
							/>
						),
					},
				);
			}, 200);
		};

		openDetailModal('', {
			title: undefined,
			size: '2xl',
			modalProps: {
				isDismissable: false,
				isDismissible: false,
				isKeyboardDismissDisabled: true,
				hideCloseButton: true,
				classNames: {
					base: 'p-0 bg-transparent shadow-none border-none max-w-2xl [&_button[aria-label="Đóng"]]:hidden',
					wrapper: 'p-0',
					body: 'p-0',
					closeButton: 'hidden',
				},
			},
			renderContent: () => (
				<RenewPackageModal
					user={user}
					onClose={() => {}} // Không cho đóng
					onProceedPayment={handleProceedPayment}
				/>
			),
		});
	};

	// ── Mở modal nâng cấp gói (PricingCards) ────────────────────────────
	const openUpgradeModal = () => {
		openDetailModal('', {
			title: 'Nâng cấp gói dịch vụ',
			size: 'full',
			modalProps: {
				isDismissable: false,
				isDismissible: false,
				isKeyboardDismissDisabled: true,
				hideCloseButton: true,
				classNames: {
					closeButton: 'hidden',
				},
			},
			renderContent: () => <PricingCards />,
		});
	};

	// ── 2. Đảm bảo modal luôn mở: nếu modalStack rỗng thì lập tức kích hoạt lại
	useEffect(() => {
		if (modalStack.length === 0) {
			if (mode === 'upgrade') {
				openUpgradeModal();
			} else {
				openRenewModal();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalStack.length, mode]);

	// Không render UI — chỉ trigger modal
	return null;
};
